import { FieldSelect, FieldText, Form, FormikForm, FormRowField } from "@skilitics/designer-ui-components";
import type { ArrayBinaryOperator, Operator } from "@skilitics/judy";

import { arrayBinaryOperators, booleanOperators, numberOperators, stringOperators } from "../../shared";
import type { ObjectArrayConditionContext } from "./types";

export type ObjectArrayConditionFormValues = {
  arrayOperator: ArrayBinaryOperator | "";
  objectOperator: Operator | "";
  value: string;
};

export type ObjectArrayConditionFormProps = Pick<
  ObjectArrayConditionContext["spec"],
  "options" | "objectPropertyType"
> & {
  initialValues: ObjectArrayConditionFormValues;
  onSubmit: (newValues: ObjectArrayConditionFormValues) => void;
  specOperator: ObjectArrayConditionContext["spec"]["arrayOperator"];
};

const objectOperatorsByPropertyType: Record<
  ObjectArrayConditionContext["spec"]["objectPropertyType"],
  ReadonlyArray<Operator>
> = {
  boolean: booleanOperators,
  number: numberOperators,
  string: stringOperators,
};

export const ObjectArrayConditionForm = ({
  initialValues,
  onSubmit,
  options,
  specOperator,
  objectPropertyType,
}: ObjectArrayConditionFormProps) => {
  const operatorOptions = arrayBinaryOperators.map((value) => ({ value, label: value }));
  const valueOptions = options?.map(({ value, label }) => ({ value, label: label ?? value }));

  const objectOperatorOptions = objectOperatorsByPropertyType[objectPropertyType].map((value) => ({
    value,
    label: value,
  }));

  return (
    <FormikForm<ObjectArrayConditionFormValues>
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      submitOnBlur
      formatStrings={formatStrings}
      dataTest="object-array-condition-form"
    >
      <Form $labelAlignment="inline">
        <FormRowField<ObjectArrayConditionFormValues> name="arrayOperator">
          <FieldSelect disabled={!!specOperator} options={operatorOptions} />
        </FormRowField>

        <FormRowField<ObjectArrayConditionFormValues> name="objectOperator">
          <FieldSelect options={objectOperatorOptions} />
        </FormRowField>

        <FormRowField<ObjectArrayConditionFormValues> name="value">
          {valueOptions && <FieldSelect options={valueOptions} />}
          {!valueOptions && <FieldText />}
        </FormRowField>
      </Form>
    </FormikForm>
  );
};

const formatStrings = {
  arrayOperatorLabel: {
    en: "Array Operator",
  },
  objectOperatorLabel: {
    en: "Object Operator",
  },
  valueLabel: {
    en: "Value",
  },
};
